/* @jsx h */
import { defineComponent, h, ref, computed, PropType } from "vue";
import { useInfiniteQuery } from "@tanstack/vue-query";
import { formatPriceInLocale } from "@lemmonhq/orders";
import { format } from "date-fns";
import {
  LemmonModal,
  ResponsiveTable,
  type ResponsiveTableColumn,
  useLocalization,
} from "@lemmonhq/uikit";
import { useAuth, useTrpc } from "@app/vue/providers/index";

type Entry = {
  id: number;
  entry_number: number;
  account_code: string;
  account_name: string;
  amount: number;
  entry_type: string;
  journal_id: number;
  journal_type: string;
  source_type: string;
  source_id: string;
  effective_at: string;
  business_date: string;
  reason: string | null;
  order_id: number | null;
  order_item_id: number | null;
  product_id: number | null;
  product_name: string | null;
  payment_id: number | null;
  gift_card_id: string | null;
  promotion_id: string | null;
  tax_name: string | null;
  tax_rate: number | string | null;
  note: string | null;
};

type Cursor = { effective_at: string; id: number };

const ENTRY_TYPES = [
  "tender",
  "gross_revenue",
  "discount",
  "promotion",
  "tax",
  "tax_discount",
  "tax_promotion",
  "gift_card_liability",
  "tip",
  "delivery_fee",
  "settlement",
  "processor_fee",
];

const JOURNAL_TYPES = [
  "sale",
  "refund",
  "void",
  "settlement",
  "processor_fee",
  "chargeback",
  "adjustment",
];

const JOURNAL_BADGE: Record<string, string> = {
  sale: "badge-success",
  refund: "badge-warning",
  void: "badge-error",
  settlement: "badge-info",
  processor_fee: "badge-ghost",
  chargeback: "badge-error",
  adjustment: "badge-ghost",
};

export const EntriesModal = defineComponent({
  name: "EntriesModal",
  props: {
    title: { type: String, required: true },
    code: { type: String, default: "" },
    category: { type: String, default: "" },
    // Drill-down context — at most one is set.
    accountCode: { type: String as PropType<string | null>, default: null },
    productId: { type: Number as PropType<number | null>, default: null },
    from: { type: String, required: true },
    to: { type: String, required: true },
    // When showing an account, the account column is redundant; show it for products.
    showAccount: { type: Boolean, default: false },
    onClose: { type: Function as PropType<() => void>, required: true },
  },
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const auth = useAuth();
    const currency = () => auth.user.value?.restaurant.currency || "CHF";

    const entryType = ref<string>("");
    const journalType = ref<string>("");

    const query = useInfiniteQuery({
      queryKey: computed(() => [
        "accounting",
        "entries",
        props.accountCode,
        props.productId,
        props.from,
        props.to,
        entryType.value,
        journalType.value,
      ]),
      queryFn: ({ pageParam }) =>
        trpc.accounting.accountEntries.query({
          account_code: props.accountCode ?? undefined,
          product_id: props.productId ?? undefined,
          from: props.from,
          to: props.to,
          entry_type: entryType.value || undefined,
          journal_type: journalType.value || undefined,
          limit: 30,
          cursor: (pageParam as Cursor | undefined) ?? undefined,
        }),
      initialPageParam: undefined as Cursor | undefined,
      getNextPageParam: (lastPage: any) =>
        lastPage.has_more ? (lastPage.next_cursor as Cursor) : undefined,
    });

    const entries = computed<Entry[]>(
      () =>
        query.data.value?.pages.flatMap((p: any) => p.data as Entry[]) ?? [],
    );

    const onScroll = (e: Event) => {
      const el = e.target as HTMLElement;
      if (
        el.scrollHeight - el.scrollTop - el.clientHeight < 300 &&
        query.hasNextPage.value &&
        !query.isFetchingNextPage.value
      ) {
        query.fetchNextPage();
      }
    };

    const describeEntry = (row: Entry): string => {
      if (row.product_name) return row.product_name;
      if (row.note) return row.note;
      if (row.tax_name) return row.tax_name;
      return t(`reports.coa.entry_type.${row.entry_type}`);
    };

    const columns = computed<ResponsiveTableColumn[]>(() => {
      const cols: ResponsiveTableColumn[] = [
        {
          key: "date",
          label: t("reports.coa.col_date"),
          render: (row: Entry) => (
            <div class="flex flex-col">
              <span class="text-sm text-base-content">
                {format(new Date(row.effective_at), "dd MMM yyyy")}
              </span>
              <span class="text-[11px] text-base-content/45">
                {format(new Date(row.effective_at), "HH:mm")}
              </span>
            </div>
          ),
          mobilePriority: "secondary",
        },
        {
          key: "journal",
          label: t("reports.coa.col_journal"),
          render: (row: Entry) => (
            <div class="flex flex-col gap-1">
              <span
                class={`badge badge-sm ${
                  JOURNAL_BADGE[row.journal_type] ?? "badge-ghost"
                }`}
              >
                {t(`reports.coa.journal_type.${row.journal_type}`)}
              </span>
              <span class="text-[11px] text-base-content/45 font-mono">
                {row.source_type}#{row.source_id}
              </span>
            </div>
          ),
          mobilePriority: "secondary",
        },
        {
          key: "description",
          label: t("reports.coa.col_description"),
          render: (row: Entry) => (
            <div class="flex flex-col gap-1 min-w-0">
              <span class="text-sm text-base-content truncate">
                {describeEntry(row)}
              </span>
              <div class="flex flex-wrap gap-1">
                <span class="badge badge-ghost badge-xs font-mono">
                  {t(`reports.coa.entry_type.${row.entry_type}`)}
                </span>
                {props.showAccount && (
                  <span class="badge badge-ghost badge-xs font-mono">
                    {row.account_code} · {row.account_name}
                  </span>
                )}
                {row.payment_id != null && (
                  <span class="badge badge-ghost badge-xs">
                    {t("reports.coa.dim_payment")} #{row.payment_id}
                  </span>
                )}
                {row.order_id != null && (
                  <span class="badge badge-ghost badge-xs">
                    {t("reports.coa.dim_order")} #{row.order_id}
                  </span>
                )}
                {row.tax_name && (
                  <span class="badge badge-ghost badge-xs">{row.tax_name}</span>
                )}
              </div>
            </div>
          ),
          mobilePriority: "primary",
        },
        {
          key: "debit",
          label: t("reports.coa.col_debit"),
          headerClass: "text-right",
          cellClass: "text-right tabular-nums",
          render: (row: Entry) =>
            row.amount > 0 ? (
              <span class="font-medium text-base-content">
                {formatPriceInLocale(row.amount, false, 2, currency())}
              </span>
            ) : (
              <span class="text-base-content/25">—</span>
            ),
          mobilePriority: "secondary",
        },
        {
          key: "credit",
          label: t("reports.coa.col_credit"),
          headerClass: "text-right",
          cellClass: "text-right tabular-nums",
          render: (row: Entry) =>
            row.amount < 0 ? (
              <span class="font-medium text-base-content">
                {formatPriceInLocale(-row.amount, false, 2, currency())}
              </span>
            ) : (
              <span class="text-base-content/25">—</span>
            ),
          mobilePriority: "secondary",
        },
      ];
      return cols;
    });

    const renderSelect = (
      value: { value: string },
      options: string[],
      labelPrefix: string,
      placeholder: string,
    ) => (
      <select
        class="select select-bordered select-sm w-full sm:w-auto"
        value={value.value}
        onChange={(e: any) => (value.value = e.target.value)}
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option value={opt} key={opt}>
            {t(`${labelPrefix}.${opt}`)}
          </option>
        ))}
      </select>
    );

    return () => (
      <LemmonModal
        title={t("reports.coa.entries_title")}
        onClose={() => props.onClose()}
        style="height: 88vh;"
      >
        <div class="flex flex-col h-full min-h-0">
          {/* Context summary + filters */}
          <div class="px-4 py-3 border-b border-base-300/50 flex flex-col gap-3">
            <div class="flex items-center gap-2 flex-wrap">
              {props.code && (
                <span class="font-mono text-xs text-base-content/50">
                  {props.code}
                </span>
              )}
              <span class="font-medium text-base-content">{props.title}</span>
              {props.category && (
                <span class="badge badge-ghost badge-sm font-mono text-[10px]">
                  {props.category}
                </span>
              )}
            </div>
            <div class="flex flex-col sm:flex-row gap-2 sm:items-center">
              {renderSelect(
                entryType,
                ENTRY_TYPES,
                "reports.coa.entry_type",
                t("reports.coa.filter_entry_type"),
              )}
              {renderSelect(
                journalType,
                JOURNAL_TYPES,
                "reports.coa.journal_type",
                t("reports.coa.filter_journal_type"),
              )}
              {(entryType.value || journalType.value) && (
                <button
                  class="btn btn-ghost btn-sm"
                  onClick={() => {
                    entryType.value = "";
                    journalType.value = "";
                  }}
                >
                  <span class="material-symbols-outlined text-base">close</span>
                  {t("reports.coa.filter_clear")}
                </button>
              )}
            </div>
          </div>

          {/* Entries list */}
          <div class="flex-1 min-h-0 overflow-y-auto" onScroll={onScroll}>
            {query.isError.value ? (
              <div class="flex flex-col items-center justify-center h-full text-error gap-3">
                <span class="material-symbols-outlined text-5xl opacity-60">
                  error_outline
                </span>
                <p class="text-sm font-medium">{t("reports.coa.error_load")}</p>
                <button
                  class="btn btn-sm btn-outline btn-error"
                  onClick={() => query.refetch()}
                >
                  {t("reports.coa.retry")}
                </button>
              </div>
            ) : (
              <ResponsiveTable
                columns={columns.value}
                rows={entries.value}
                rowKey={(row: Entry) => row.id}
                loading={query.isLoading.value}
                loadingMore={query.isFetchingNextPage.value}
                emptyMessage={t("reports.coa.empty_entries")}
              />
            )}
          </div>
        </div>
      </LemmonModal>
    );
  },
});
